<script setup>
import {ref, reactive, computed, watch, nextTick, provide} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useConfigData} from "@/composables/useConfigData.js";
import HotOrders from "@/views/orders/Details/hotOrders/HotOrders.vue";
import FloatLabel from "primevue/floatlabel";
import {Button, InputGroup, InputGroupAddon, InputText, Textarea} from "primevue";
import Select from "primevue/select";
import StateField from "@/views/orders/Shared/StateField.vue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import {numeric, required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import {Icon} from "@iconify/vue";
import {TabGroup, TabList, Tab, TabPanels, TabPanel} from "@headlessui/vue";
import Tooltip from "@/components/Tooltip/index.vue";
import {showMessage} from "@/utils/showMessage.js";
import Loading from "@/components/Icon/Loading.vue";
import Customers from "@/components/Customers.vue";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";
import CustomerProfile from "@/components/CustomerProfile/index.vue";
import {useCustomerStore} from "@/stores/customer.js";
import {useUserStore} from "@/stores/user.js";
import {gregorianDate} from "@/utils/gregorianDate.js";
import PriceCalculatorModal from "@/views/orders/Details/Boost/PriceCalculatorModal.vue";

/* tabs */
const tabs = [
  {
    title: "Details",
  },
  {
    title: "Attachments",
  },
];
const selectedTab = ref(0)

/* attachments */
const attachments = ref(null)

/* ---------- Stores ---------- */
const ordersStore = useMainOrdersStore()
const userStore = useUserStore();
const customerStore = useCustomerStore();
const route = useRoute()
const router = useRouter()

/* ---------- Loading status ---------- */
const loading = ref(false)
const saving = ref(false)
const isUploadingAttachment = ref(false)

/* ---------- Order object ---------- */
const order = reactive({
  state: 'New',
  title: '',
  store: '',
  game: '',
  g2gSellID: '',
  link: '',
  price: '',
  takersCut: '0',
  tax: '',
  commissionFee: '',
  withdrawalFee: '',
  income: '',
  revenue: '',
  customer: '',
  customerID: '',
  takers: '',
  notes: '',
})

const rules = computed(() => {
  return {
    state: {required},
    store: {required},
    game: {required},
    title: {required},
    price: {required, numeric},
    tax: {required, numeric},
    customerID: {required},
  };
});
const $vulidate = useVuelidate(rules, order);

const selectedCustomer = ref({})
const isShowCustomerProfileModal = ref(false);
const canShowCustomerProfile = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))

/* Tab content height */
const detailsContent = ref()
const tabHeight = ref(0)

/* ---------- Related to checkup interval ---------- */
const checkupOptions = [
  {value: 0, label: 'Off'},
  {value: 3600000, label: 'Every 1 hour'},
  {value: 3600000 * 2, label: 'Every 2 hours'},
  {value: 3600000 * 4, label: 'Every 4 hours'},
  {value: 3600000 * 6, label: 'Every 6 hours'},
  {value: 3600000 * 8, label: 'Every 8 hours'},
  {value: 3600000 * 12, label: 'Every 12 hours'},
  {value: 3600000 * 24, label: 'Every 24 hours'},
]

/* ---------- Select options ---------- */
const {stores, games} = useConfigData('Boost')

/* ---------- Selected store ---------- */
const selectedStoreObj = computed(() => stores.value.find(a => a.store === order.store) || null)

const priceCalculatorStores = new Set([
  'WoWMarket (Dawn)',
  'WoWMarket (Gold)',
  'WoWMarket (Oblivion)',
  'WoWMarket (Sylvanas)',
])
const storesWithZeroDefaults = new Set([
  'Direct',
  ...priceCalculatorStores,
])
const isShowPriceCalculatorModal = ref(false)

watch(() => order.store, (store, previousStore) => {
  if (store !== previousStore) {
    if (priceCalculatorStores.has(store)) {
      isShowPriceCalculatorModal.value = true
    }

    if (storesWithZeroDefaults.has(store)) {
      if (order.tax === '' || order.tax === null || order.tax === undefined) {
        order.tax = 0
      }
      if (order.g2gSellID === '' || order.g2gSellID === null || order.g2gSellID === undefined) {
        order.g2gSellID = 0
      }
    }
  }
})

/* ---------- Income and profit calculations ---------- */
const recalc = () => {
  const price = Number(order.price)
  const tax = Number(order.tax)
  const takersCut = Number(order.takersCut)

  order.commissionFee = selectedStoreObj.value?.commissionFee ?? 0
  order.withdrawalFee = selectedStoreObj.value?.withdrawalFee ?? 0

  /* -------- income -------- */
  if (order.store && price) {
    const incomeNum = ((price * (1 - Number(order.commissionFee))) - tax) * (1 - Number(order.withdrawalFee))
    order.income = incomeNum.toFixed(2)
  } else {
    order.income = ''
  }

  /* -------- revenue -------- */
  if (order.income !== '' && order.takersCut != null) {
    let revenueNum = Number(order.income) - takersCut
    if (order.store === 'DealHub EUR DAWN') {
      revenueNum *= 0.95
    }
    order.revenue = revenueNum.toFixed(2) // ← رشته
  } else {
    order.revenue = ''
  }
}

/* ---------- Always stay up to date! ---------- */
watch(
    [() => order.store, () => order.price, () => order.takersCut, () => order.tax],
    recalc,
    {immediate: true}          // Immediately after mount and every time it changes
)

/* Object filter: Keep only keys that have the value truthy */
function buildPayload() {
  const payload = {}
  Object.entries(order).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) payload[k] = v
  })

  // delete checkupInterval if it is 0
  /*if (payload.checkupInterval === 0) {
    delete payload.checkupInterval
  }*/

  if (attachments.value.length) {
    payload.attachments = {paymentReceipts: attachments.value}
  }
  return payload
}

async function onSave() {
  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  // 2) Filter filled fields
  const payload = buildPayload()
  if (Object.keys(payload).length === 0) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  // 3) User verification
  //const ok = await showAlert('You are adding new order?', 'Are you sure?', 'Yes', 'No')
  //if (!ok) return

  // 4) API call
  try {
    saving.value = true
    const finalPayload = (({takersCut, ...rest}) => rest)(payload);
    const res = await ordersStore.addOrder('Boost', finalPayload)
    if (res === true) {
      showMessage('New Order added!', 'success')
      backToOrders();
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

/* Tab content height management */
const updateTabHeight = async () => {
  if (selectedTab.value === 0) {
    await nextTick()
    const detailsH = detailsContent.value?.scrollHeight
    tabHeight.value = detailsH + 16
  }
}

// Update tab height when validation errors appear/disappear
watch(() => $vulidate.value.$errors, () => {
  nextTick(() => updateTabHeight())
}, {deep: true})

// Update when order fields change (affects income/revenue display, etc.)
watch(() => order, () => {
  nextTick(() => updateTabHeight())
}, {deep: true})


function backToOrders() {
  router.push({name: 'boost-orders'})
}

function validateForm() {
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  return true
}

const addOrder = async (order) => {
  const payload = {}
  Object.entries(order).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) payload[k] = v
  })

  // delete checkupInterval if it is 0
  /*if (payload.checkupInterval === 0) {
    delete payload.checkupInterval
  }*/

  if (attachments.value.length) {
    payload.attachments = {paymentReceipts: attachments.value}
  }

  const finalOrder = (({takersCut, ...rest}) => rest)(payload);
  const res = await ordersStore.addOrder2('Boost', finalOrder)
  if (res.result === true) {
    showMessage(`New Order added!`, 'success');
    return res.orderID
  } else {
    showMessage(res.error, 'error');
    return false
  }
}

const getSelectedCustomer = (customer) => {
  selectedCustomer.value = customer || null
  /*order.customer = customer
      ? customer.name ? customer.name : customer.storeID
      : null*/
  order.customerID = customer ? customer.id : null
}

function showCustomerProfileModal(user) {
  if (user.isLoading) return;
  if (canShowCustomerProfile.value) {
    customerStore.setSelectedCustomer(user);
    isShowCustomerProfileModal.value = true
  }
}

function applyCalculatedPrice(value) {
  order.price = value
  isShowPriceCalculatorModal.value = false
}

provide('isShowCustomerProfileModal', isShowCustomerProfileModal)
provide('isShowPriceCalculatorModal', isShowPriceCalculatorModal)
</script>

<template>
  <div class="pb-20 md:pb-0 md:h-full">
    <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
      <div class="space-y-4 text-white/80 text-sm h-full">
        <!-- Loading mode -->
        <div v-if="loading" class="flex items-center justify-center h-60">
          <Icon icon="eos-icons:bubble-loading" class="size-12"/>
        </div>
        <TabPanels v-else class="h-full">
          <div class="flex flex-col gap-2 h-full">
            <div class="flex w-full">
              <div class="grid grid-cols-5 gap-4 h-full w-full">
                <div class="col-span-4 flex flex-row justify-between items-center">
                  <div class="flex flex-row gap-2 items-center">
                    <Tooltip placement="top" arrow>
                      <template #button>
                        <div class="back-btn" @click="backToOrders">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                               class="w-4 h-4 2xl:w-6 2xl:h-6">
                            <path fill="currentColor"
                                  d="m7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z"/>
                          </svg>
                        </div>
                      </template>
                      <span>{{ $t('message.back') }}</span>
                    </Tooltip>
                    <TabList class="">
                      <Tab
                          v-slot="{ selected }"
                          as="template"
                          v-for="(item, i) in tabs"
                          :key="i"
                      >
                        <button
                            :class="[
                              selected
                                ? 'dark:text-white text-slate-100 bg-slate-600 dark:bg-primary-700'
                                : 'text-slate-700 bg-slate-300 dark:bg-slate-700 dark:text-slate-300',
                            ]"
                            class="text-sm font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-6 py-2 transition duration-150 rounded-none first:rounded-l-2xl last:rounded-r-2xl  "
                        >
                          {{ item.title }}
                        </button>
                      </Tab>
                    </TabList>
                  </div>
                  <div class="flex flex-row items-center gap-2">
                    <StateField v-model="order.state" type="add"></StateField>
                  </div>
                </div>
                <div class="col-span-1">
                  <h6 class="text-center text-base py-2 bg-slate-100 dark:bg-slate-800 rounded-lg h-full">
                    Add Boost Order
                  </h6>
                </div>
              </div>
            </div>
            <div class="flex-1 grid grid-cols-5 gap-4 h-full">
              <div class="col-span-4 flex flex-col relative h-full overflow-hidden">
                <hr class="border-0 h-0.5 bg-slate-800">
                <div class="flex relative overflow-hidden">
                  <TabPanel :unmount="false" :style="{ height: tabHeight ? tabHeight + 'px' : 'auto' }"
                            class="flex-1 flex flex-col gap-4 relative h-full overflow-auto custom-scrollbar-2 pt-4">
                    <div ref="detailsContent" class="grid grid-cols-12 gap-4">
                      <div class="col-span-12 flex flex-row items-start gap-4">
                        <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                          <FloatLabel variant="on">
                            <InputText id="title" v-model="order.title" autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       :class="{ 'has-error': $vulidate.title.$error }"/>
                            <label for="title" class="!bg-slate-900"
                                   :class="{ 'focus:text-red-300': $vulidate.title.$error }">Title</label>
                          </FloatLabel>
                          <template v-if="$vulidate.title.$error">
                            <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.title.required.$response">
                              This field is required
                            </p>
                          </template>
                        </div>
                        <!-- ================== Store ================== -->
                        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-max">
                          <FloatLabel class="w-full custom-select" :class="{ 'has-error': $vulidate.store.$error }"
                                      variant="on">
                            <Select v-model="order.store" inputId="store" :options="stores" optionLabel="store"
                                    option-value="store"
                                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                    :class="[$vulidate.store.$error ? '!border !border-red-300' : '!border-slate-700']"
                                    input-class="!text-xs 2xl:!text-sm"
                                    overlayClass="custom-select-overlay !z-[900]"
                                    filter autoFilterFocus/>
                            <label for="store" class="!bg-slate-900">Store</label>
                          </FloatLabel>
                          <template v-if="$vulidate.store.$error">
                            <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.store.required.$response">
                              This field is required
                            </p>
                          </template>
                        </div>
                        <!-- ================== Game ================== -->
                        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-max">
                          <FloatLabel class="w-full custom-select"
                                      :class="{ 'has-error': $vulidate.game.$error }"
                                      variant="on">
                            <Select v-model="order.game" inputId="game" :options="games" optionLabel="game"
                                    option-value="game"
                                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                    :class="[$vulidate.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                                    input-class="!text-xs 2xl:!text-sm"
                                    overlayClass="custom-select-overlay !z-[900]"
                                    filter autoFilterFocus/>
                            <label for="game" class="!bg-slate-900">Game</label>
                          </FloatLabel>
                          <template v-if="$vulidate.game.$error">
                            <p class="text-red-300 mt-1 text-xs"
                               v-if="!$vulidate.game.required.$response">
                              This field is required
                            </p>
                          </template>
                        </div>
                        <!-- ================== Checkup ================== -->
<!--                        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
                          <FloatLabel class="w-full custom-select"
                                      variant="on">
                            <Select v-model="order.checkupInterval" inputId="checkup" :options="checkupOptions"
                                    optionLabel="label"
                                    option-value="value"
                                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                    input-class="!text-xs 2xl:!text-sm"
                                    :virtualScrollerOptions="{ itemSize: 38 }"
                                    filter autoFilterFocus/>
                            <label for="checkup" class="!bg-slate-900">Checkup</label>
                          </FloatLabel>
                        </div>-->
                      </div>
                      <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText id="g2gSellID" v-model="order.g2gSellID" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                          <label for="g2gSellID" class="!bg-slate-900">Sell ID</label>
                        </FloatLabel>
                      </div>
                      <div class="col-span-8 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on">
                            <InputText id="link" v-model="order.link" autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                            <label for="link" class="!bg-slate-900">Link</label>
                          </FloatLabel>
                          <Button class="!bg-transparent !border-slate-700 border-l-0 !text-xs !px-2 !py-0">
                            <a :href="order?.link" target="_blank"
                               class="text-blue-400 whitespace-nowrap hover:bg-slate-800 px-2 rounded-xl py-1">
                              Open Link
                            </a>
                          </Button>
                        </InputGroup>
                      </div>
                      <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on"
                                      :class="{ 'has-error': $vulidate.price.$error }">
                            <InputText v-allow-digits-dot id="price" v-model="order.price" autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       :class="{ 'has-error has-addon': $vulidate.price.$error }"/>
                            <label for="price" class="!bg-slate-900"
                                   :class="{ 'has-error': $vulidate.price.$error }">Price</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">$</InputGroupAddon>
                        </InputGroup>
                        <template v-if="$vulidate.price.$error">
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.price.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.price.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>
                      <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on">
                            <InputText v-allow-digits-dot id="takersCut" :value="order.takersCut" autocomplete="off"
                                       readonly disabled
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       :class="{'p-filled': !!order.takersCut }"/>
                            <label for="takersCut" class="!bg-slate-900">Takers Cut</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">$</InputGroupAddon>
                        </InputGroup>
                      </div>
                      <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on" :class="{ 'has-error': $vulidate.tax.$error }">
                            <InputText v-allow-digits-dot id="tax" v-model="order.tax" autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       :class="{ 'has-error has-addon': $vulidate.tax.$error }"/>
                            <label for="tax" class="!bg-slate-900"
                                   :class="{ 'has-error': $vulidate.tax.$error }">Tax</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">$</InputGroupAddon>
                        </InputGroup>
                        <template v-if="$vulidate.tax.$error">
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tax.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tax.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>
                      <div class="col-span-4 flex flex-col gap-1 text-xs 2xl:text-sm">
                        <div class="flex flex-row gap-2 items-center text-xs">
                          <span class="text-[4px]">⬤</span>
                          <p class="whitespace-nowrap">Commission Fee:</p>
                          <div v-if="order?.commissionFee" class="w-full">
                            {{
                              order?.commissionFee ? (order.commissionFee) * 100 + ' %' : '&nbsp'
                            }}
                          </div>
                          <div v-else class="w-full">
                            Store not selected
                          </div>
                        </div>
                        <div class="flex flex-row gap-2 items-start text-xs">
                          <span class="text-[4px]">⬤</span>
                          <p class="text-xs whitespace-nowrap">Withdrawal Fee:</p>
                          <div v-if="order?.withdrawalFee" class="w-full">
                            {{
                              order?.withdrawalFee ? (order.withdrawalFee) * 100 + ' %' : '&nbsp'
                            }}
                          </div>
                          <div v-else class="w-full">
                            Store not selected
                          </div>
                        </div>
                      </div>
                      <div class="col-span-8 flex flex-col gap-1 text-xs 2xl:text-sm">
                        <div v-if="order?.income" class="flex flex-row gap-2 items-start text-xs">
                          <span class="text-[4px]">⬤</span>
                          <p class="text-xs">Income:</p>
                          <div class="w-full">
                            {{ order?.income ? '$ ' + order.income : '&nbsp' }}
                          </div>
                        </div>
                        <div v-if="order?.revenue" class="flex flex-row gap-2 items-start text-xs">
                          <span class="text-[4px]">⬤</span>
                          <p class="text-xs">Revenue:</p>
                          <div class="w-full">
                            {{ order?.revenue ? '$ ' + order.revenue : '&nbsp' }}
                          </div>
                        </div>
                      </div>
                      <div class="col-span-4 flex flex-row gap-2 text-xs 2xl:text-sm">
                        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                          <Customers v-model="selectedCustomer" @update:modelValue="getSelectedCustomer" appendTo="body"
                                     label="Customer" :required="true"/>
                        </div>
                      </div>
                      <div v-if="selectedCustomer?.id"
                           class="col-span-8 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md px-2 py-1 h-[32px] 2xl:h-[40px]">
                        <div @click.stop="showCustomerProfileModal(selectedCustomer)"
                             :class="['h-full flex items-center', canShowCustomerProfile ? 'cursor-zoom-in' : 'cursor-default']">
                          <div
                              class="flex items-center justify-center rounded-full border border-slate-700 h-full aspect-square">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                              <path fill="currentColor"
                                    d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15c0-1.09 1.01-1.85 2.7-1.85c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61c0 2.31 1.91 3.46 4.7 4.13c2.5.6 3 1.48 3 2.41c0 .69-.49 1.79-2.7 1.79c-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55c0-2.84-2.43-3.81-4.7-4.4"/>
                            </svg>
                          </div>
                        </div>
                        <div class="flex-1 grid grid-cols-2 gap-1">
                          <div class="flex flex-col">
                            <div class="flex flex-row gap-6 text-xs">
                              <div>{{ selectedCustomer.name || selectedCustomer.storeID }}</div>
                              <div v-if="selectedCustomer.rating" class="flex flex-row items-center gap-0.5">
                                <span class="flex flex-row items-end">
                                  {{ selectedCustomer.rating }}⭐ ({{ selectedCustomer.ratingCount }})
                                </span>
                              </div>
                            </div>
                            <div class="flex flex-row w-max text-xs text-slate-400">
                              <div>{{ selectedCustomer.discordUsername ?? "ــــــ" }}</div>
                              <div>
                                {{
                                  "&nbsp- " + (selectedCustomer.discordDisplayName ? selectedCustomer.discordDisplayName : "ــــــ")
                                }}
                              </div>
                            </div>
                          </div>
                          <div class="flex flex-col text-xs">
                            <div>{{ selectedCustomer.verificationState ?? "ــــــ" }}</div>
                            <div>
                              {{
                                selectedCustomer.registrationTime ? gregorianDate(selectedCustomer.registrationTime, 'DD MMM YYYY - HH:mm') : ''
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                  <TabPanel :unmount="false" :style="{ height: tabHeight ? tabHeight + 'px' : 'auto' }"
                            class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2 pt-4">
                    <!-- Only changes -->
                    <Attachments
                        id="addOrder"
                        sizeClass="w-24 h-24"
                        fontSize="text-xs"
                        :count="16"
                        :only-changes="true"
                        v-model:uploading="isUploadingAttachment"
                        @update:payload="val => (attachments = val)"
                    />
                  </TabPanel>
                </div>
                <HotOrders :items="[]" :order="order" orderType="Boost" :add-order="addOrder"
                           :validateForm="validateForm" class="pt-4"/>
              </div>
              <div class="col-span-1 flex flex-col h-full overflow-hidden">
                <hr class="border-0 h-0.5 bg-slate-800">
                <div class="flex flex-col h-full overflow-hidden gap-4 pt-4">
                  <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm h-full">
                    <FloatLabel variant="on" class="h-full">
                      <Textarea v-auto-dir id="notes" v-model="order.notes" rows="5" style="resize: none"
                                class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 h-full"/>
                      <label for="notes" class="!bg-slate-900">Notes</label>
                    </FloatLabel>
                  </div>
                  <hr class="border-0 h-0.5 bg-slate-800">
                  <div class="grid gap-2">
                    <button @click="onSave" class="btn btn-outline-primary py-2 text-xs 2xl:text-sm" :disabled="saving">
                      <span v-if="saving"
                            class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                        <Loading/>
                      </span>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanels>
      </div>
    </TabGroup>
    <PriceCalculatorModal
        v-if="isShowPriceCalculatorModal"
        :store="order.store"
        @apply="applyCalculatedPrice"
    />
    <CustomerProfile v-if="isShowCustomerProfileModal" :userID="customerStore.selectedCustomer.id"/>
  </div>
</template>

<style scoped>
.input {
  @apply w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring focus:border-blue-500;
}

.back-btn {
  @apply h-6 2xl:h-8 w-6 2xl:w-8 bg-slate-200 dark:bg-slate-700 dark:text-slate-200 text-slate-600 flex flex-col justify-center items-center text-base rounded-full cursor-pointer;
}
</style>
